/**
 * Role Manager Page (ORCH-333, ORCH-21 §11)
 *
 * Purpose: Where roles are listed and managed: GLOBAL's standard roles and the
 *          active tenant's own. Tabs for the roles, the permission catalog, the
 *          security dimensions, and a member's effective access (ORCH-337).
 * Access: the route needs the Role Manager area; buttons follow role.create /
 *         role.edit / role.delete. A GLOBAL role is read-only outside GLOBAL and
 *         a system role always is; the server enforces the same rules.
 * Dependencies: authzService, MDTable (manual mode)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Chip, Link, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  ContentCopy as DuplicateIcon,
  Home as HomeIcon,
  Lock as LockIcon,
  SecurityOutlined as RoleIcon,
} from '@mui/icons-material';

import SkeletonPage from '../../../components/layout/SkeletonPage';
import MDTable from '../../../components/md-table';
import {
  NotificationSnackbar,
  RowActionButton,
  RowActions,
  StatusChip,
} from '../../../components/common';
import { DeleteConfirmationDialog } from '../../../components/common/DeleteConfirmationDialog';
import { useAuth } from '../../../context/AuthContext';
import { PermissionKey } from '../../../constants/permissionKeys';
import { authzService, roleErrorMessage } from '../../../services/authzService';
import {
  Dimension,
  ExplainableUser,
  PermissionCatalog,
  Role,
  RoleAdjustment,
} from '../../../types/authz';
import { TableColumn } from '../../../types/table';
import DuplicateRoleDialog from './DuplicateRoleDialog';
import EffectiveAccessPanel from './EffectiveAccessPanel';

const GLOBAL = 'GLOBAL';
const OTHER_AREA = 'other';

type RoleRow = Role & { id: string; permission_count: number; scope_label: string };

interface Notice {
  message: string;
  severity: 'success' | 'error' | 'info';
}

/**
 * Can anyone change this role? Mirrors RoleService._writable (ORCH-415): a GLOBAL
 * role is a standard role, locked for everyone wherever it is viewed from.
 */
export const isRoleWritable = (role: Role, _activeTenantId?: string): boolean =>
  !role.is_system && role.tenant_id !== GLOBAL;

/** Plain words for what a duplicate changed to fit the tenant. */
export const describeAdjustments = (
  adjusted: RoleAdjustment[],
  labels: Record<string, string> = {}
): string => {
  const words: Record<RoleAdjustment['change'], string> = {
    removed: 'left out',
    capped_to_read: 'made read-only',
    values_dropped: 'lost values that no longer exist',
    removed_no_live_values: 'left out (its values no longer exist)',
    removed_needs_parent: 'left out (needs its parent permission)',
  };
  return adjusted.map((a) => `${labels[a.key] ?? a.key} ${words[a.change]}`).join('; ');
};

const EFFECTIVE_ACCESS_TAB = 3;
export const EFFECTIVE_ACCESS_TAB_PARAM = 'effective-access';

const RoleManagerPage: React.FC = () => {
  const { state, can } = useAuth();
  const navigate = useNavigate();
  const activeTenantId = state.user?.activeTenant?.tenant_id;

  // `?tab=effective-access` opens that tab, on the member passed in router state:
  // the User Manager's Effective Access action links here (ORCH-336, PRD UM-11)
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const linkedMember = (location.state as { member?: ExplainableUser } | null)?.member ?? null;
  const [tab, setTab] = useState(
    searchParams.get('tab') === EFFECTIVE_ACCESS_TAB_PARAM ? EFFECTIVE_ACCESS_TAB : 0
  );
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [duplicating, setDuplicating] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const [busy, setBusy] = useState(false);

  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <AdminIcon fontSize="inherit" /> },
    { label: 'Role Manager', icon: <RoleIcon fontSize="inherit" /> },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [roleList, permissionCatalog, dimensionList] = await Promise.all([
        authzService.listRoles(),
        authzService.getCatalog(),
        authzService.getDimensions(),
      ]);
      setRoles(roleList);
      setCatalog(permissionCatalog);
      setDimensions(dimensionList);
    } catch (e) {
      setError(roleErrorMessage(e, 'Failed to load roles'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows: RoleRow[] = useMemo(
    () =>
      roles.map((r) => ({
        ...r,
        id: r.role_id,
        permission_count: r.permissions.length,
        scope_label: r.tenant_id === GLOBAL ? 'Standard' : 'This tenant',
      })),
    [roles]
  );

  const catalogLabels = useMemo(
    () =>
      Object.fromEntries(
        [...(catalog?.permissions ?? []), ...(catalog?.modules ?? [])].map((d) => [d.key, d.label])
      ),
    [catalog]
  );

  // Each permission's Area label (ORCH-415): groups the reference and Effective access
  const areaLabels = useMemo(() => {
    const moduleLabels = Object.fromEntries((catalog?.modules ?? []).map((m) => [m.key, m.label]));
    return Object.fromEntries(
      (catalog?.permissions ?? []).map((p) => [p.key, (p.area && moduleLabels[p.area]) || 'Other'])
    );
  }, [catalog]);

  // Permissions Reference rows, grouped by the Area the catalog puts each key under
  const referenceRows = useMemo(() => {
    return (catalog?.permissions ?? [])
      .filter((p) => p.kind === 'data' || p.resource !== 'module')
      .map((p) => ({
        ...p,
        id: p.key,
        area_key: p.area ?? OTHER_AREA,
        area_label: areaLabels[p.key] ?? 'Other',
        granted_by: roles.filter((r) => r.permissions.some((rp) => rp.key === p.key)).length,
      }))
      .sort((a, b) => a.area_label.localeCompare(b.area_label) || a.label.localeCompare(b.label));
  }, [catalog, roles, areaLabels]);

  const runAction = useCallback(
    async (action: () => Promise<unknown>, success: string) => {
      setBusy(true);
      try {
        await action();
        setNotice({ message: success, severity: 'success' });
        await load();
      } catch (e) {
        setNotice({ message: roleErrorMessage(e, 'The change was refused'), severity: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  const handleSetActive = useCallback(
    (role: Role, active: boolean) => {
      runAction(
        () => authzService.setActive(role.role_id, active),
        active ? `${role.name} is active again` : `${role.name} is disabled`
      );
    },
    [runAction]
  );

  const handleDelete = async () => {
    if (!deleting) return;
    const role = deleting;
    await runAction(() => authzService.deleteRole(role.role_id), `${role.name} was deleted`);
    setDeleting(null);
  };

  // Duplicate and edit: the copy opens in the editor, which says what changed to fit the tenant
  const handleDuplicated = (copy: Role, adjusted: RoleAdjustment[]) => {
    setDuplicating(null);
    navigate(`/admin/role-manager/${copy.role_id}`, {
      state: {
        notice: adjusted.length
          ? `${copy.name} created. To fit this tenant: ${describeAdjustments(adjusted, catalogLabels)}`
          : `${copy.name} created`,
        severity: adjusted.length ? 'info' : 'success',
      },
    });
  };

  const columns: TableColumn[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Role',
        flex: 1,
        renderCell: (value: string, row: RoleRow) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{value}</Typography>
            {row.is_system && (
              <Tooltip title="System role: can't be changed in the Role Manager">
                <Chip
                  icon={<LockIcon />}
                  label="System"
                  size="small"
                  variant="outlined"
                  data-testid={`system-badge-${row.name}`}
                />
              </Tooltip>
            )}
          </Box>
        ),
      },
      { field: 'description', headerName: 'Description', flex: 1.5 },
      {
        field: 'scope_label',
        headerName: 'Scope',
        width: 130,
        renderCell: (value: string, row: RoleRow) => (
          <Chip
            label={value}
            size="small"
            color={row.tenant_id === GLOBAL ? 'default' : 'primary'}
            variant={row.tenant_id === GLOBAL ? 'outlined' : 'filled'}
          />
        ),
      },
      { field: 'permission_count', headerName: 'Permissions', type: 'number', width: 120 },
      {
        field: 'assigned_user_count',
        headerName: 'People',
        type: 'number',
        width: 100,
        renderCell: (value: number, row: RoleRow) =>
          value > 0 ? (
            <Link
              href={`/admin/role-manager/${row.role_id}#people`}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/admin/role-manager/${row.role_id}#people`);
              }}
              data-testid={`role-people-link-${row.name}`}
            >
              {value}
            </Link>
          ) : (
            value
          ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (value: string) => (
          <StatusChip
            status={value === 'inactive' ? 'disabled' : value}
            label={value === 'inactive' ? 'Inactive' : undefined}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (_: unknown, row: RoleRow) => {
          const writable = isRoleWritable(row, activeTenantId);
          const canEdit = writable && can(PermissionKey.ROLE_EDIT);
          const canDelete = writable && can(PermissionKey.ROLE_DELETE);
          // No role is authored in GLOBAL (ORCH-415), so nothing is duplicated there
          const canDuplicate = can(PermissionKey.ROLE_CREATE) && activeTenantId !== GLOBAL;
          if (!canEdit && !canDelete && !canDuplicate) {
            return (
              <Typography
                variant="caption"
                color="text.disabled"
                data-testid={`role-readonly-${row.name}`}
              >
                {row.tenant_id === GLOBAL ? 'Standard roles are read-only' : 'Read-only here'}
              </Typography>
            );
          }
          return (
            <RowActions
              entityLabel="Role"
              rowLabel={row.name}
              testIdPrefix={`role-${row.name}`}
              rowTestId={`role-actions-${row.name}`}
              onEdit={canEdit ? () => navigate(`/admin/role-manager/${row.role_id}`) : undefined}
              secondaryActions={
                canDuplicate && (
                  <RowActionButton
                    label="Duplicate Role"
                    ariaLabel={`Duplicate Role ${row.name}`}
                    testId={`role-${row.name}-duplicate`}
                    onClick={() => setDuplicating(row)}
                    icon={<DuplicateIcon fontSize="small" />}
                  />
                )
              }
              deactivation={
                canEdit
                  ? {
                      variant: 'disable',
                      isActive: row.status === 'active',
                      onToggle: () => handleSetActive(row, row.status !== 'active'),
                      disabled: busy,
                    }
                  : undefined
              }
              onDelete={canDelete ? () => setDeleting(row) : undefined}
              deleteDisabled={busy || row.assigned_user_count > 0}
              deleteDisabledReason={
                row.assigned_user_count > 0
                  ? `Remove it from its ${row.assigned_user_count} holder(s) first`
                  : undefined
              }
            />
          );
        },
      },
    ],
    [activeTenantId, busy, can, handleSetActive, navigate]
  );

  return (
    <SkeletonPage pageTitle="Role Manager" breadcrumbs={breadcrumbs}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Roles decide which areas people can open, what they can do, and on which records. Standard
        roles are shared by every tenant; duplicate one to make a version for this tenant.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, value: number) => setTab(value)}
          aria-label="role manager tabs"
        >
          <Tab
            label="Roles"
            data-testid="tab-roles"
            id="role-manager-tab-0"
            aria-controls="role-manager-panel-0"
          />
          <Tab
            label="Permissions Reference"
            data-testid="tab-permissions"
            id="role-manager-tab-1"
            aria-controls="role-manager-panel-1"
          />
          <Tab
            label="Dimensions Reference"
            data-testid="tab-dimensions"
            id="role-manager-tab-2"
            aria-controls="role-manager-panel-2"
          />
          <Tab
            label="Effective access"
            data-testid="tab-effective-access"
            id="role-manager-tab-3"
            aria-controls="role-manager-panel-3"
          />
        </Tabs>
      </Box>

      {tab === 0 && (
        <div role="tabpanel" id="role-manager-panel-0" aria-labelledby="role-manager-tab-0">
          {activeTenantId === GLOBAL && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="role-manager-global-readonly">
              Standard roles are changed by a release, not here. Duplicate one from a tenant to
              customise it.
            </Alert>
          )}
          <MDTable
            mode="manual"
            manual={{ columns, title: 'Roles', description: `${rows.length} role(s)` }}
            data={rows}
            loading={loading}
            onRefresh={load}
            onRowClick={(row: RoleRow) => navigate(`/admin/role-manager/${row.role_id}`)}
            onAddNew={() => navigate('/admin/role-manager/new')}
            showAddButton={can(PermissionKey.ROLE_CREATE) && activeTenantId !== GLOBAL}
            showRefreshButton
            selectable={false}
            sortable
            filterable
            defaultSortModel={[{ field: 'name', sort: 'asc' }]}
          />
        </div>
      )}

      {tab === 1 && (
        <div role="tabpanel" id="role-manager-panel-1" aria-labelledby="role-manager-tab-1">
          <MDTable
            mode="manual"
            manual={{
              columns: [
                { field: 'label', headerName: 'Permission', flex: 1 },
                { field: 'description', headerName: 'Description', flex: 2 },
                { field: 'key', headerName: 'Key', flex: 1 },
                {
                  field: 'scoped',
                  headerName: 'Scoped',
                  width: 90,
                  renderCell: (value: boolean) => (value ? 'Yes' : 'No'),
                },
                {
                  field: 'notes',
                  headerName: 'Notes',
                  width: 160,
                  renderCell: (_: unknown, row: { baseline: boolean; global_only: boolean }) =>
                    [row.baseline && 'Everyone has it', row.global_only && 'GLOBAL only']
                      .filter(Boolean)
                      .join(' · '),
                },
                {
                  field: 'granted_by',
                  headerName: 'Roles granting it',
                  type: 'number',
                  width: 150,
                },
              ],
              title: 'Permissions Reference',
              description: 'The fixed list of things a role can grant, grouped by area',
            }}
            data={referenceRows}
            loading={loading}
            selectable={false}
            sortable
            filterable
            groupByField="area_key"
            groupLabelField="area_label"
            collapsibleGroups
            defaultGroupsExpanded={false}
          />
        </div>
      )}

      {tab === 2 && (
        <div role="tabpanel" id="role-manager-panel-2" aria-labelledby="role-manager-tab-2">
          <MDTable
            mode="manual"
            manual={{
              columns: [
                { field: 'label', headerName: 'Dimension', flex: 1 },
                { field: 'root_fielddef_id', headerName: 'Top-level list', flex: 1 },
                {
                  field: 'hierarchical',
                  headerName: 'Levels',
                  width: 140,
                  renderCell: (value: boolean) => (value ? 'Hierarchy' : 'Flat list'),
                },
                {
                  field: 'match_mode',
                  headerName: 'A record with several values matches when',
                  flex: 1.5,
                  renderCell: (value: string) =>
                    value === 'cover_all' ? 'every value is in scope' : 'any value is in scope',
                },
              ],
              title: 'Dimensions Reference',
              description: 'What scoped permissions are filtered by',
            }}
            data={dimensions.map((d) => ({ ...d, id: d.key }))}
            loading={loading}
            selectable={false}
          />
        </div>
      )}

      {tab === EFFECTIVE_ACCESS_TAB && (
        <div role="tabpanel" id="role-manager-panel-3" aria-labelledby="role-manager-tab-3">
          <EffectiveAccessPanel
            dimensions={dimensions}
            labels={catalogLabels}
            areaLabels={areaLabels}
            initialMember={linkedMember}
          />
        </div>
      )}

      {duplicating && (
        <DuplicateRoleDialog
          role={duplicating}
          existingNames={roles.filter((r) => r.tenant_id === activeTenantId).map((r) => r.name)}
          onClose={() => setDuplicating(null)}
          onDuplicated={handleDuplicated}
        />
      )}

      <DeleteConfirmationDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        itemName={deleting?.name ?? ''}
        itemType="role"
        loading={busy}
      />

      <NotificationSnackbar
        open={Boolean(notice)}
        onClose={() => setNotice(null)}
        message={notice?.message ?? ''}
        severity={notice?.severity}
      />
    </SkeletonPage>
  );
};

export default RoleManagerPage;
